// src/pages/Login.js
import { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { collection, getDocs, getDoc, doc, query, where } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import "./Login.css";
import ContactAdminBox from "../admin/ContactAdminBox";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  async function handleLogin(e) {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Reload to get latest emailVerified status from Firebase Auth
      await user.reload();
      const refreshedUser = auth.currentUser;

      // Check Firestore for user role
      let userRole = null;
      let userDoc = null;

      try {
        // Try to get user document using UID as document ID
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          userDoc = { id: userDocSnap.id, ...userDocSnap.data() };
          userRole = userDocSnap.data().role;
        } else {
          // If not found by UID, query by uid field
          const usersRef = collection(db, "users");
          const q = query(usersRef, where("uid", "==", user.uid));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            userDoc = { id: docSnap.id, ...docSnap.data() };
            userRole = docSnap.data().role;
          }
        }
      } catch (firestoreError) {
        console.error("Error fetching user role:", firestoreError);
        // If we can't get the role, default to user role
        userRole = "user";
      }

      // Check email verification
      // Users created by admin may have emailVerified in Firestore but not in Auth
      const isVerifiedInAuth = refreshedUser.emailVerified;
      const isVerifiedInFirestore = userDoc?.emailVerified === true;
      const isEmailVerified = isVerifiedInAuth || isVerifiedInFirestore;

      if (!isEmailVerified) {
        // Email not verified - redirect to verification page
        setErrorMsg("Please verify your email before logging in.");
        setTimeout(() => {
          navigate("/verify-email", { replace: true });
        }, 2000);
        return;
      }

      // Email verified - route based on role
      if (userRole === "admin") {
        // Only admins go to admin dashboard
        navigate("/admin", { replace: true });
      } else {
        // Editors and users go to dashboard
        // Editors will see trail management section at the bottom
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err.message);

      if (err.code === "auth/invalid-credential") {
        setErrorMsg("Invalid email or password.");
      } else if (err.code === "auth/user-not-found") {
        setErrorMsg("No account found with this email.");
      } else if (err.code === "auth/wrong-password") {
        setErrorMsg("Incorrect password.");
      } else if (err.code === "auth/too-many-requests") {
        setErrorMsg("Too many failed attempts. Please try again later.");
      } else {
        setErrorMsg("Unable to sign in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-overlay"></div>

      <div className="login-container">
        <h2>Sign In</h2>

        <form onSubmit={handleLogin} className="login-form">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />

          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                // Eye slash icon (hide password)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                // Eye icon (show password)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          <button className="login-btn" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>

          {errorMsg && <p className="login-error">{errorMsg}</p>}
        </form>

        <Link to="/forgot-password" className="login-register-link">
          Forgot password?
        </Link>

        <Link to="/register" className="login-register-link">
          Don't have an account? Register
        </Link>
        {/* Contact Admin Box */}
          <ContactAdminBox />
      </div>
      
    </div>
  );
}